import { useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { prefersStill, useLoadingStyle } from './useLoadingStyle'
import { bucketedLinks, capDpr, fullscreenStarfieldPoints, shouldDrawFrame, stepDots } from './loaderDraw'
import { COLOR_PICKER_SWATCHES } from './colorPickerConstants'
import { getNlsString } from '../../nls/nlsRuntime'
import './loaders.css'

export interface FullscreenLoaderArtProps {
  className?: string
  style?: CSSProperties
}

export const FullscreenLoaderArt = ({
  className,
  style,
}: FullscreenLoaderArtProps) => {
  const loadingStyle = useLoadingStyle()
  const still = prefersStill()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const shineRef = useRef<HTMLSpanElement | null>(null)

  const word = getNlsString('NLSAuthBrandName')

  const starfieldPoints = useMemo(() => {
    return loadingStyle === 'starfield' ? fullscreenStarfieldPoints() : []
  }, [loadingStyle])

  // Wordmark shine measurement
  useEffect(() => {
    if (loadingStyle !== 'starfield' || still) return
    const el = shineRef.current
    if (!el) return

    const measure = () => {
      const rect = el.getBoundingClientRect()
      const w = rect.width
      if (!w) return
      el.style.setProperty('--w', `${w}px`)
      el.style.setProperty('--win', `${Math.round(w * 0.42)}px`)
    }

    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [loadingStyle, still])

  // Constellation canvas loop
  useEffect(() => {
    if (loadingStyle !== 'constellation') return
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = capDpr(typeof window !== 'undefined' ? window.devicePixelRatio : 1)
    const FRAME_MS = 1000 / 30

    let rafId = 0
    let lastTime = 0
    let isHidden = typeof document !== 'undefined' ? document.hidden : false

    const cs = getComputedStyle(canvas)
    const lineColor = cs.getPropertyValue('--loader-line').trim() || cs.getPropertyValue('--border').trim() || 'currentColor'
    const dotColor = cs.getPropertyValue('--loader-accent').trim() || COLOR_PICKER_SWATCHES[0]

    let w = 0
    let h = 0
    let dots: Array<{ x: number; y: number; vx: number; vy: number }> = []

    const drawFrame = () => {
      ctx.clearRect(0, 0, w, h)

      const buckets = bucketedLinks(dots, 90, 6)
      ctx.lineWidth = 0.7
      ctx.strokeStyle = lineColor
      for (let k = 0; k < buckets.length; k++) {
        const L = buckets[k]
        if (!L.length) continue
        ctx.globalAlpha = (k + 0.5) / buckets.length
        ctx.beginPath()
        for (let i = 0; i < L.length; i += 4) {
          ctx.moveTo(L[i], L[i + 1])
          ctx.lineTo(L[i + 2], L[i + 3])
        }
        ctx.stroke()
      }

      ctx.globalAlpha = 1
      ctx.fillStyle = dotColor
      ctx.beginPath()
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i]
        ctx.moveTo(d.x + 1.6, d.y)
        ctx.arc(d.x, d.y, 1.6, 0, Math.PI * 2)
      }
      ctx.fill()
    }

    const fit = () => {
      const rect = canvas.getBoundingClientRect()
      w = rect.width || (typeof window !== 'undefined' ? window.innerWidth : 800)
      h = rect.height || (typeof window !== 'undefined' ? window.innerHeight : 600)

      canvas.width = Math.floor(w * dpr)
      canvas.height = Math.floor(h * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      const dotCount = Math.max(24, Math.min(80, Math.floor((w * h) / 900)))
      dots = Array.from({ length: dotCount }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
      }))

      if (still) {
        drawFrame()
      }
    }

    fit()

    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        fit()
      })
      ro.observe(canvas)
    }

    if (still) {
      return () => {
        cancelAnimationFrame(rafId)
        ro?.disconnect()
      }
    }

    const onVisibilityChange = () => {
      isHidden = document.hidden
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    const loop = (t: number) => {
      rafId = requestAnimationFrame(loop)
      if (isHidden || !w || !h) return
      if (!shouldDrawFrame(t, lastTime, FRAME_MS)) return
      lastTime = t
      stepDots(dots, w, h, 2)
      drawFrame()
    }

    rafId = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(rafId)
      ro?.disconnect()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [loadingStyle, still])

  const rootClass = [
    'dsLoader',
    'dsLoader--fullscreen',
    `dsLoader--${loadingStyle}`,
    still ? 'dsLoader--still' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClass} style={style}>
      <div className="dsLoader__stage" aria-hidden="true">
        {loadingStyle === 'constellation' ? (
          <canvas ref={canvasRef} className="dsLoader__canvas" />
        ) : (
          <>
            <div className="dsLoader__dots">
              {starfieldPoints.map((pt, i) => (
                <span
                  key={i}
                  className={pt.className}
                  style={{
                    left: `${pt.x}%`,
                    top: `${pt.y}%`,
                    animationDelay: `${pt.delay}s`,
                  }}
                />
              ))}
            </div>
            <span ref={shineRef} className="dsLoader__shine">
              {word}
              {!still && (
                <span className="dsLoader__shineWin">
                  <span className="dsLoader__shineTxt">{word}</span>
                </span>
              )}
            </span>
          </>
        )}
      </div>
    </div>
  )
}
